import React from 'react';
import * as ReactQuery from 'react-query';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { createSelector } from '@reduxjs/toolkit';
import { createGenericContext } from '../../utils/create-generic-context';
import { RootState } from '../../global';
import api from '../../global/api';
import {
  BuyOrderExtraServices,
  BuyProcessState,
  CostCalculationApiResponse,
  MunicipalityType,
} from './buy-process-form-types';

export interface BuyProcessFormContextInterface {
  handleNextStep: () => void;
  municipalities?: MunicipalityType[];
  id: string;
  goToRequestedStep: (
    predicate: (() => Promise<boolean>) | boolean,
    onSuccess?: () => any
  ) => void;
  setRequestedStep: React.Dispatch<React.SetStateAction<number>>;
}

export const [useBuyProcessForm, BuyProcessFormContextProvider] = createGenericContext<BuyProcessFormContextInterface>();

export const BuyProcessStep: Record<
  keyof (BuyProcessState & { submissionInfo: any; summary: any }),
  string
> = {
  submissionInfo: 'Inlämningsinformation',
  clientInfo: 'Beställare',
  companyDetails: 'Bolagsuppgifter',
  ownerShares: 'Ägare / Aktiekapital',
  reservation: 'Förbehåll',
  representatives: 'Företrädare',
  fiscalYear: 'Räkenskapsår',
  auditor: 'Revisor',
  proxies: 'Fullmakter',
  costStatement: 'Kostnadssammanställning',
  summary: 'Granska Order',
};

export const serviceFeeUnavailableMessage = 'Vi kan inte få det aktuella priset. Uppdatera sidan.';

export function useIsVerticalStepper() {
  return useMediaQuery('(max-width: 1100px)');
}

export function selectExtraServices(orderId: string) {
  return createSelector(
    (state: RootState) => state.buyOrderEdit[orderId].companyDetails.taxHelp,
    (state: RootState) => state.buyOrderEdit[orderId].companyDetails.nameReview,
    (state: RootState) =>
      state.buyOrderEdit[orderId].ownerShares.beneficialOwnerHelp,
    (taxHelp, nameReview, beneficialOwnerHelp) => ({
      taxHelp,
      nameReview,
      beneficialOwnerHelp,
    }),
  );
}

const QUERY_KEY_EXTRA_SERVICES = 'extra-services';
export function useFetchExtraServicesCost(options: BuyOrderExtraServices) {
  return ReactQuery.useQuery(QUERY_KEY_EXTRA_SERVICES, async () => {
    const response = await api.get<CostCalculationApiResponse>('/cost', {
      params: {
        beneficial: options.beneficial,
        nameReview: options.nameReview,
        taxHelp: options.taxHelp,
      },
    });
    return response.data;
  });
}

export function getExtraServicesCost() {
  return ReactQuery.queryCache.getQueryData<CostCalculationApiResponse>(
    QUERY_KEY_EXTRA_SERVICES,
  );
}
